import type { AppStateEntity } from '@/domains/app/app.entity';
import type { NavigationStateEntity } from '@/domains/navigation/navigation.entity';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AppStoreEntity } from '../models';

const defaultNavigationState: NavigationStateEntity = {
  breadcrumbs: [],
  currentTitle: '',
};

const defaultAppState: AppStateEntity = {
  titleMaintainer: '',
  navigation: defaultNavigationState,
};

/**
 * Store principal de la aplicación
 * Gestiona el título del mantenedor y el sistema de navegación breadcrumb
 * Persiste: titleMaintainer y navigation en localStorage
 */
export const useAppStore = create<AppStoreEntity>()(
  persist(
    (set, get) => ({
      ...defaultAppState,

      /**
       * Establece el título del mantenedor actual
       *
       * @param title - Título a mostrar en el header del mantenedor
       */
      setTitle: (title: string) => {
        set({ titleMaintainer: title });
      },

      /**
       * Resetea el título del mantenedor a su valor por defecto
       */
      resetTitle: () => {
        set({ titleMaintainer: defaultAppState.titleMaintainer });
      },

      /**
       * Agrega una nueva miga de pan (breadcrumb) al stack de navegación
       * Actualiza automáticamente el título actual
       *
       * @param breadcrumb - Objeto con información de la navegación (title, path, etc.)
       */
      addBreadcrumb: (breadcrumb) => {
        const { navigation } = get();
        const updatedBreadcrumbs = [...navigation.breadcrumbs, breadcrumb];
        set({
          navigation: {
            breadcrumbs: updatedBreadcrumbs,
            currentTitle: breadcrumb.title,
          },
        });
      },

      /**
       * Navega hacia atrás en el stack de breadcrumbs hasta un índice específico
       * Elimina todos los breadcrumbs posteriores al índice objetivo
       *
       * @param targetIndex - Índice del breadcrumb al que regresar (0-based)
       */
      navigateBackToIndex: (targetIndex) => {
        const { navigation } = get();
        const updatedBreadcrumbs = navigation.breadcrumbs.slice(
          0,
          targetIndex + 1,
        );
        const newTitle =
          updatedBreadcrumbs[updatedBreadcrumbs.length - 1]?.title || '';
        set({
          navigation: {
            breadcrumbs: updatedBreadcrumbs,
            currentTitle: newTitle,
          },
        });
      },

      /**
       * Limpia completamente el stack de navegación
       * Resetea breadcrumbs y título actual
       */
      clearNavigationStack: () => {
        set({
          navigation: defaultNavigationState,
        });
      },

      /**
       * Actualiza solo el título de navegación actual sin modificar breadcrumbs
       *
       * @param title - Nuevo título para la navegación actual
       */
      setCurrentNavigationTitle: (title) => {
        const { navigation } = get();
        set({
          navigation: {
            ...navigation,
            currentTitle: title,
          },
        });
      },

      /**
       * Obtiene el estado completo de navegación
       *
       * @returns Estado actual de navegación con breadcrumbs y título
       */
      getNavigationState: () => {
        return get().navigation;
      },
    }),
    {
      name: '@app',
      partialize: (state) => ({
        titleMaintainer: state.titleMaintainer,
        navigation: state.navigation,
      }),
    },
  ),
);
